// components/RecordingStudio/BlockEditor.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Pause,
  Sliders,
  Type,
  Volume2,
  Clock,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Mic,
  Loader,
  Sparkles,
  Zap,
} from "lucide-react";
import ScriptGeneratorModal from "./ScriptGeneratorModal";

// ── Slider ───────────────────────────────────────────────────────────────────
const Slider = ({
  label,
  value,
  min,
  max,
  step = 0.1,
  unit = "",
  onChange,
}) => {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-medium text-gray-500 uppercase tracking-widest">
          {label}
        </span>
        <span
          className="text-[11px] font-mono tabular-nums px-1.5 py-0.5 rounded-md"
          style={{ background: "rgba(139,92,246,0.12)", color: "#a78bfa" }}
        >
          {value > 0 && unit !== "x" ? "+" : ""}
          {value}
          {unit}
        </span>
      </div>
      <div className="relative h-5 flex items-center">
        <div
          className="absolute inset-x-0 h-[3px] rounded-full"
          style={{ background: "rgba(255,255,255,0.06)" }}
        />
        <div
          className="absolute left-0 h-[3px] rounded-full"
          style={{
            width: `${pct}%`,
            background: "linear-gradient(90deg, #7c3aed, #a855f7)",
          }}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="absolute inset-0 w-full opacity-0 cursor-pointer"
        />
        {/* thumb indicator */}
        <div
          className="absolute w-3.5 h-3.5 rounded-full border-2 border-violet-400 bg-[#0a0c12] shadow-md shadow-violet-500/30 pointer-events-none"
          style={{ left: `calc(${pct}% - 7px)` }}
        />
      </div>
    </div>
  );
};

// ── Section ───────────────────────────────────────────────────────────────────
const Section = ({ title, icon: Icon, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/[0.05]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon size={12} className="text-violet-500" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.1em]">
            {title}
          </span>
        </div>
        {open ? (
          <ChevronUp size={10} className="text-gray-700" />
        ) : (
          <ChevronDown size={10} className="text-gray-700" />
        )}
      </button>
      {open && <div className="px-4 pb-4 space-y-4">{children}</div>}
    </div>
  );
};

// ── Avatar gradient (mirrors VoiceBlock) ─────────────────────────────────────
const AVATAR_GRADIENTS = [
  "from-violet-500 via-purple-500 to-fuchsia-500",
  "from-cyan-500 via-blue-500 to-indigo-500",
  "from-emerald-500 via-teal-500 to-cyan-500",
  "from-rose-500 via-pink-500 to-fuchsia-500",
  "from-amber-500 via-orange-500 to-rose-500",
  "from-lime-500 via-green-500 to-emerald-500",
];

const resolveDisplayName = (block) =>
  block.voiceDisplayName || block.voiceName || "Voice";

// ── Main ─────────────────────────────────────────────────────────────────────
const BlockEditor = ({
  block,
  blockIndex = 0,
  avatarImageUrl,
  onUpdate,
  onSynthesize,
}) => {
  const [text, setText] = useState(block?.text || "");
  const [speed, setSpeed] = useState(block?.speed || 1.0);
  const [pitch, setPitch] = useState(block?.pitch || 0);
  const [volume, setVolume] = useState(block?.volumeGainDb || 0);
  const [pause, setPause] = useState(block?.pauseBefore || 0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showScriptGen, setShowScriptGen] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (block) {
      setText(block.text || "");
      setSpeed(block.speed || 1.0);
      setPitch(block.pitch || 0);
      setVolume(block.volumeGainDb || 0);
      setPause(block.pauseBefore || 0);
    }
    setIsPreviewing(false);
  }, [block?.id]);

  useEffect(() => {
    if (!block?.audioUrl) setIsPreviewing(false);
  }, [block?.audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPreviewing) audio.play().catch(() => setIsPreviewing(false));
    else {
      audio.pause();
      audio.currentTime = 0;
    }
  }, [isPreviewing]);

  if (!block) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6 gap-3">
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{
            background: "rgba(139,92,246,0.1)",
            border: "1px solid rgba(139,92,246,0.15)",
          }}
        >
          <Mic size={22} className="text-violet-600" />
        </div>
        <div>
          <p className="text-sm font-medium text-gray-500">No block selected</p>
          <p className="text-xs text-gray-700 mt-0.5">Click a block to edit</p>
        </div>
      </div>
    );
  }

  const displayName = resolveDisplayName(block);
  const isProcessing = block.status === "processing";
  const avatarGradient = AVATAR_GRADIENTS[blockIndex % AVATAR_GRADIENTS.length];

  const handleUpdate = (patch) =>
    onUpdate?.({
      text,
      speed,
      pitch,
      volumeGainDb: volume,
      pauseBefore: pause,
      ...patch,
    });

  const wordCount = text.split(" ").filter(Boolean).length;
  const estDuration = Math.ceil(wordCount / 2.5);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-white/[0.05] flex items-center gap-3">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg overflow-hidden bg-gradient-to-br ${avatarGradient}`}
          style={{
            boxShadow: "0 4px 12px rgba(124,58,237,0.3)",
          }}
        >
          {avatarImageUrl ? (
            <img
              src={avatarImageUrl}
              alt={displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <span
            style={{ display: avatarImageUrl ? "none" : "flex" }}
            className="w-full h-full items-center justify-center text-sm font-bold"
          >
            {displayName[0]}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate leading-none">
            {displayName}
          </p>
          <p className="text-[10px] text-gray-600 mt-0.5 font-mono">
            {block.languageCode || "en-GB"}
          </p>
        </div>
        <span
          className={`text-[9px] px-2 py-1 rounded-full font-bold uppercase tracking-wider ${
            block.status === "completed"
              ? "bg-emerald-500/10 text-emerald-400"
              : block.status === "processing"
                ? "bg-amber-500/10 text-amber-400"
                : block.status === "failed"
                  ? "bg-red-500/10 text-red-400"
                  : "bg-white/5 text-gray-600"
          }`}
        >
          {block.status || "draft"}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {/* Script */}
        <Section title="Script" icon={Type}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => handleUpdate({ text })}
            rows={5}
            className="w-full p-3 rounded-xl text-xs text-gray-200 placeholder-gray-700 resize-none focus:outline-none leading-relaxed transition-colors"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "rgba(139,92,246,0.4)";
            }}
            onBlurCapture={(e) => {
              e.target.style.borderColor = "rgba(255,255,255,0.06)";
            }}
            placeholder="Enter your script here…"
          />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-gray-700 font-mono">
                {text.length} chars
              </span>
              <span className="text-gray-800">·</span>
              <span className="text-[9px] text-gray-700 font-mono">
                ~{estDuration}s
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              {/* ── NEW: AI Script button ── */}
              <button
                onClick={() => setShowScriptGen(true)}
                className="flex items-center gap-1 text-[9px] font-semibold text-violet-500 hover:text-violet-300 transition-colors px-2 py-1 rounded-md hover:bg-violet-500/10"
              >
                <Sparkles size={9} />
                Ask AI
              </button>
              <button
                onClick={() => handleUpdate({ text })}
                className="text-[9px] font-semibold text-violet-500 hover:text-violet-300 transition-colors px-2 py-1 rounded-md hover:bg-violet-500/10"
              >
                Apply
              </button>
            </div>
          </div>

          {/* ── NEW: Script Generator Modal ── */}
          {showScriptGen && (
            <ScriptGeneratorModal
              voiceDisplayName={resolveDisplayName(block)}
              onClose={() => setShowScriptGen(false)}
              onUseScript={(script) => {
                setText(script);
                handleUpdate({ text: script });
              }}
            />
          )}
        </Section>

        {/* Voice */}
        <Section title="Voice Shaping" icon={Sliders}>
          <Slider
            label="Speed"
            value={speed}
            min={0.25}
            max={4.0}
            step={0.05}
            unit="x"
            onChange={(v) => {
              setSpeed(v);
              handleUpdate({ speed: v });
            }}
          />
          <Slider
            label="Pitch"
            value={pitch}
            min={-20}
            max={20}
            step={0.5}
            unit="st"
            onChange={(v) => {
              setPitch(v);
              handleUpdate({ pitch: v });
            }}
          />
          <button
            onClick={() => {
              setSpeed(1.0);
              setPitch(0);
              handleUpdate({ speed: 1.0, pitch: 0 });
            }}
            className="flex items-center gap-1.5 text-[10px] text-gray-700 hover:text-gray-400 transition-colors py-0.5"
          >
            <RefreshCw size={9} />
            Reset to defaults
          </button>
        </Section>

        {/* Volume */}
        <Section title="Volume" icon={Volume2}>
          <Slider
            label="Gain"
            value={volume}
            min={-20}
            max={20}
            step={0.5}
            unit="dB"
            onChange={(v) => {
              setVolume(v);
              handleUpdate({ volumeGainDb: v });
            }}
          />
        </Section>

        {/* Timing */}
        <Section title="Timing" icon={Clock} defaultOpen={false}>
          <Slider
            label="Pre-pause"
            value={pause}
            min={0}
            max={10}
            step={0.25}
            unit="s"
            onChange={(v) => {
              setPause(v);
              handleUpdate({ pauseBefore: v });
            }}
          />
          <p className="text-[9px] text-gray-700 leading-relaxed">
            Silence inserted before this block begins playing.
          </p>
        </Section>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-white/[0.05] space-y-2">
        {block.audioUrl && (
          <>
            <button
              onClick={() => setIsPreviewing((p) => !p)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs text-gray-400 hover:text-white transition-all"
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              {isPreviewing ? <Pause size={12} /> : <Play size={12} />}
              {isPreviewing ? "Stop preview" : "Preview audio"}
            </button>
            <audio
              ref={audioRef}
              src={block.audioUrl}
              onEnded={() => setIsPreviewing(false)}
            />
          </>
        )}
        <button
          onClick={onSynthesize}
          disabled={isProcessing}
          className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: isProcessing
              ? "rgba(124,58,237,0.4)"
              : "linear-gradient(135deg, #7c3aed, #6d28d9)",
            boxShadow: isProcessing
              ? "none"
              : "0 4px 16px rgba(124,58,237,0.3)",
          }}
        >
          {isProcessing ? (
            <>
              <Loader size={12} className="animate-spin" />
              Synthesizing…
            </>
          ) : (
            <>
              <Zap size={12} />
              Synthesize Voice
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BlockEditor;
